import type { Booking } from '@/lib/types/booking'

type BookingLike = Pick<Booking, 'source' | 'metadata'>

type BookingMetadata = Record<string, unknown>

export type BookingKind = 'reservation' | 'order'
export type FulfillmentMethod = 'dine_in' | 'pickup' | 'delivery'

function parseMetadata(metadata: unknown): BookingMetadata {
  if (!metadata) return {}
  if (typeof metadata === 'object' && !Array.isArray(metadata)) {
    return metadata as BookingMetadata
  }

  if (typeof metadata !== 'string') return {}

  try {
    const parsed = JSON.parse(metadata) as unknown
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as BookingMetadata
    }
  } catch {
    // ignore malformed metadata
  }

  return {}
}

export function getBookingMetadata(booking: BookingLike | null | undefined): BookingMetadata {
  return parseMetadata(booking?.metadata)
}

export function getBookingKind(booking: BookingLike | null | undefined): BookingKind {
  const metadata = getBookingMetadata(booking)
  if (metadata.bookingKind === 'order') return 'order'
  if (metadata.bookingKind === 'reservation') return 'reservation'
  if (booking?.source === 'collection') return 'order'
  return 'reservation'
}

export function isOrderBooking(booking: BookingLike | null | undefined): boolean {
  return getBookingKind(booking) === 'order'
}

export function getFulfillmentMethod(booking: BookingLike | null | undefined): FulfillmentMethod {
  if (!isOrderBooking(booking)) {
    return 'dine_in'
  }

  const metadata = getBookingMetadata(booking)
  if (metadata.fulfillmentMethod === 'delivery') return 'delivery'
  if (metadata.fulfillmentMethod === 'pickup' || metadata.fulfillmentMethod === 'collection') {
    return 'pickup'
  }

  return 'pickup'
}

export function isDeliveryOrder(booking: BookingLike | null | undefined): boolean {
  return getFulfillmentMethod(booking) === 'delivery'
}
